import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { SimpleTable } from 'components/tables'
import { LinkedStatus } from './LinkedStatus'
import { LinkedButton } from './LinkedButton'
import { TwoColumn } from 'components/layout'
import { workdayLogo } from 'components/integration-logos'

interface WorkdaySectionProps {
  onLink: () => void
  onUnlink: () => void
  workdayProjectId: string | undefined
  workdayProjectName: string | undefined
}

interface WorkdayRow {
  name: string
  linked: boolean
  workdayProjectName: string | undefined
  id: number | string
}

const columnHelper = createColumnHelper<WorkdayRow>()

export const WorkdaySection: FC<WorkdaySectionProps> = ({
  onLink,
  onUnlink,
  workdayProjectId,
  workdayProjectName,
}) => {
  const { t } = useTranslation()

  const data: WorkdayRow[] = useMemo(() => {
    return [
      {
        name: t('features.projectsAdmin.projectDetails.workday'),
        linked: Boolean(workdayProjectId),
        id: workdayProjectId || t('common.notAvailable'),
        workdayProjectName,
      },
    ]
  }, [workdayProjectId, workdayProjectName, t])

  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      header: t('features.projectsAdmin.projectDetails.workday'),
      cell: ({ getValue }) => (
        <span className="gap-3 flex items-center">
          <img
            src={workdayLogo}
            alt={t('features.projectsAdmin.projectDetails.workdayLogoAlt')}
            className="w-5 h-5"
          />
          {getValue()}
        </span>
      ),
    }),
    columnHelper.accessor((row) => row.linked, {
      id: 'status',
      header: t('common.status'),
      cell: ({ getValue }) => <LinkedStatus linked={getValue()} />,
    }),
    columnHelper.accessor(
      (row) =>
        row.linked && row.workdayProjectName
          ? row.workdayProjectName
          : t('common.notAvailable'),
      {
        id: 'projectName',
        header: t('features.projectsAdmin.projectDetails.projectName'),
      },
    ),
    columnHelper.accessor('id', {
      header: t('features.projectsAdmin.projectDetails.projectId'),
    }),
    columnHelper.accessor((row) => row.linked, {
      id: 'action',
      header: t('common.action'),
      cell: ({ getValue }) => (
        <LinkedButton linked={getValue()} onLink={onLink} onUnlink={onUnlink} />
      ),
    }),
  ] as ColumnDef<WorkdayRow>[]

  return (
    <TwoColumn
      label={t('features.projectsAdmin.projectDetails.workday')}
      description={t(
        'features.projectsAdmin.projectDetails.workdayDescription',
      )}
    >
      <SimpleTable
        className="w-[825px]"
        columns={columns}
        data={data}
        defaultSortColumn="name"
      />
    </TwoColumn>
  )
}
